/** Headline time-range chips — today / yesterday / 7d / 30d / custom.
 *
 *  Shared between the Dashboard and the Analytics → Competition page so
 *  both surface the same window semantics. Time-window resolution on
 *  the backend (``_resolve_window`` in app/routes/stats.py) is also
 *  shared, so what the chip says here is what the SQL window actually
 *  filters on — Tbilisi-midnight-anchored calendar boundaries for
 *  every fixed chip, exact ``[from, to]`` inclusive for custom. */
import { useEffect, useRef, useState } from "react";

export type Range = "today" | "yesterday" | "7d" | "30d" | "custom";

/** Custom-range picker state. ``from`` / ``to`` are local-calendar
 *  dates in ``YYYY-MM-DD`` form (matches what an ``<input type="date">``
 *  produces and what the backend expects for the ``from``/``to`` query
 *  params on /api/stats/* and /api/analytics/*). */
export interface CustomRange {
  from: string;
  to: string;
}

/** Today's date in ``YYYY-MM-DD`` using the *local* calendar — what the
 *  date picker shows and what the user reads on the wall clock. */
export function todayLocalISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function addDaysLocal(iso: string, delta: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Compact "May 15" rendering for the picker chip label. ISO date in,
 *  human-readable out, local-calendar interpretation so the chip
 *  matches what the user typed. */
export function formatPickerDate(iso: string): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Build the query-string fragment for a (range, customRange) pair so
 *  every page that calls a window-aware endpoint composes its URL
 *  identically. Returns a ``URLSearchParams`` ready for ``toString()``
 *  or further mutation. */
export function buildRangeQuery(
  range: Range,
  customRange: CustomRange,
  extra?: Record<string, string>,
): URLSearchParams {
  const qs = new URLSearchParams({ range, ...(extra ?? {}) });
  if (range === "custom") {
    qs.set("from", customRange.from);
    qs.set("to", customRange.to);
  }
  return qs;
}

export function RangeChips({
  value,
  onChange,
  customRange,
  onCustomChange,
}: {
  value: Range;
  onChange: (next: Range) => void;
  customRange: CustomRange;
  onCustomChange: (next: CustomRange) => void;
}) {
  const fixedOpts: { key: Exclude<Range, "custom">; label: string }[] = [
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "7d", label: "7 days" },
    { key: "30d", label: "30 days" },
  ];
  const [pickerOpen, setPickerOpen] = useState(false);
  // Draft state for the popover — the actual ``customRange`` only
  // updates on Apply so the page doesn't refetch on every keystroke
  // while the user is mid-edit.
  const [draft, setDraft] = useState<CustomRange>(customRange);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const todayIso = todayLocalISO();

  // Sync the draft when the parent's customRange changes externally
  // (e.g. switching from a preset back to custom).
  useEffect(() => {
    setDraft(customRange);
  }, [customRange.from, customRange.to]);

  // Close the picker on outside-click / Escape.
  useEffect(() => {
    if (!pickerOpen) return;
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPickerOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [pickerOpen]);

  const applyCustom = () => {
    let { from, to } = draft;
    // Guard: swap if the user typed ``to`` before ``from``.
    if (from && to && to < from) {
      [from, to] = [to, from];
    }
    // Guard: clamp to today so we don't query the future.
    if (to && to > todayIso) to = todayIso;
    if (from && from > todayIso) from = todayIso;
    if (!from || !to) return;
    onCustomChange({ from, to });
    onChange("custom");
    setPickerOpen(false);
  };

  // Label shown inside the Custom chip — either "Custom" when nothing's
  // applied yet, or the active dates (e.g. "May 1 – May 15") so the
  // user can read the picked range without opening the popover.
  const customChipLabel =
    value === "custom"
      ? `${formatPickerDate(customRange.from)} – ${formatPickerDate(customRange.to)}`
      : "Custom";

  return (
    <div ref={wrapperRef} className="relative inline-flex items-center gap-1.5">
      <div className="inline-flex bg-white/[0.03] border border-white/10 rounded-lg p-0.5 text-xs">
        {fixedOpts.map((o) => (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.key)}
            className={`px-3 py-1 rounded transition ${
              value === o.key
                ? "bg-accent-500/20 text-accent-200"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            {o.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setPickerOpen((o) => !o)}
          className={`px-3 py-1 rounded transition inline-flex items-center gap-1.5 ${
            value === "custom"
              ? "bg-accent-500/20 text-accent-200"
              : "text-slate-400 hover:text-slate-200"
          }`}
          aria-haspopup="dialog"
          aria-expanded={pickerOpen}
        >
          <CalendarIcon />
          <span>{customChipLabel}</span>
        </button>
      </div>

      {pickerOpen && (
        <div
          role="dialog"
          aria-label="Pick a custom date range"
          className="absolute right-0 top-[calc(100%+6px)] z-40 w-[300px] rounded-xl bg-ink-900 border border-white/10 shadow-card p-3"
        >
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Custom range
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">
                From
              </span>
              <input
                type="date"
                value={draft.from}
                max={draft.to || todayIso}
                onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
                className="bg-ink-850 border border-white/10 rounded-md px-2 py-1.5 text-[12px] text-slate-100 focus:outline-none focus:border-accent-500/60"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">
                To
              </span>
              <input
                type="date"
                value={draft.to}
                min={draft.from}
                max={todayIso}
                onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
                className="bg-ink-850 border border-white/10 rounded-md px-2 py-1.5 text-[12px] text-slate-100 focus:outline-none focus:border-accent-500/60"
              />
            </label>
          </div>
          {/* Shortcut presets — the most common "I want the last N days"
              picks pre-populated so the user doesn't have to click two
              date inputs for them. They write through the draft, not
              the live state, so the user can preview them in the
              inputs before applying. */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {[
              { label: "Last 14d", days: 14 },
              { label: "Last 60d", days: 60 },
              { label: "Last 90d", days: 90 },
            ].map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() =>
                  setDraft({
                    from: addDaysLocal(todayIso, -(p.days - 1)),
                    to: todayIso,
                  })
                }
                className="text-[11px] px-2 py-1 rounded border border-white/10 text-slate-400 hover:text-slate-100 hover:border-white/20"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setPickerOpen(false)}
              className="text-[11.5px] text-slate-400 hover:text-slate-200 px-2 py-1"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={applyCustom}
              disabled={!draft.from || !draft.to}
              className="text-[11.5px] font-medium px-2.5 py-1 rounded-md bg-accent-600 hover:bg-accent-500 text-white transition-colors disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CalendarIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
